import { useRef, useState } from "react";
import { Box, Upload, Trash2, Check, Copy } from "lucide-react";
import { Modal, Button, Switch, Slider, Select, Loader, useToast } from "@/shared/ui";
import { useEmbedOptions } from "./embedOptionsState";
import type { EmbedOptions, VaultModel } from "@/features/vault/vaultApi";

/**
 * Focused white-label / viewer settings for a model's embed. Edits the embed's
 * `options` (applied host-side in the public runtime) with a live mock preview.
 */
export default function EmbedSettings({ model, onClose }: { model: VaultModel; onClose: () => void }) {
  const { toast } = useToast();
  // Shared with the studio's Embed / Branding panels. Two surfaces editing the
  // same stored object from two copies of this logic is the drift railConfig
  // already documents for the Poses and Advanced panels.
  const { opts, set, save: persist, saving, loading, uploadLogo, snippet } =
    useEmbedOptions(model._id);
  const [copied, setCopied] = useState(false);
  const logoInput = useRef<HTMLInputElement>(null);

  const save = async () => {
    if (await persist()) {
      toast({ tone: "success", title: "Embed settings saved" });
      onClose();
    }
  };

  const copyHtml = () => {
    if (!snippet) return;
    void navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  return (
    <Modal open onClose={onClose} title="Embed settings" description={model.name} size="xl">
      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader label="Loading…" />
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
          {/* Controls */}
          <div className="space-y-6">
            <Group title="Layout">
              <Switch label="Responsive" checked={!!opts.responsive} onChange={(e) => set({ responsive: e.target.checked })} />
              {!opts.responsive && (
                <div className="mt-3 flex gap-3">
                  <NumberField label="Width" value={opts.width ?? 1280} onChange={(v) => set({ width: v })} />
                  <NumberField label="Height" value={opts.height ?? 720} onChange={(v) => set({ height: v })} />
                </div>
              )}
            </Group>

            <Group title="Viewer">
              <Switch label="Show title" checked={!!opts.showTitle} onChange={(e) => set({ showTitle: e.target.checked })} />
              <Switch
                label="Auto-rotate"
                checked={!!opts.autoRotate}
                onChange={(e) => set({ autoRotate: e.target.checked })}
                className="mt-3"
              />
              {opts.autoRotate && (
                <div className="mt-3">
                  <Slider
                    label="Rotation speed"
                    showValue
                    unit="×"
                    min={0.5}
                    max={4}
                    step={0.5}
                    value={opts.autoRotateSpeed ?? 1}
                    onChange={(e) => set({ autoRotateSpeed: Number(e.target.value) })}
                  />
                </div>
              )}
              <div className="mt-4">
                <p className="mb-2 text-[12px] font-medium text-ink-700">Background</p>
                <div className="flex items-center gap-3">
                  <Select
                    label="Type"
                    value={opts.background?.type ?? "transparent"}
                    onChange={(e) =>
                      set({ background: { type: e.target.value as "transparent" | "color", color: opts.background?.color ?? "#ffffff" } })
                    }
                    options={[
                      { value: "transparent", label: "Transparent" },
                      { value: "color", label: "Solid color" },
                    ]}
                  />
                  {opts.background?.type === "color" && (
                    <input
                      type="color"
                      value={opts.background?.color ?? "#ffffff"}
                      onChange={(e) => set({ background: { type: "color", color: e.target.value } })}
                      className="h-10 w-12 shrink-0 cursor-pointer rounded-lg border border-ink-200"
                    />
                  )}
                </div>
              </div>
            </Group>

            <Group title="Branding">
              <div className="flex items-center gap-3">
                <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-xl border border-ink-200 bg-ink-50">
                  {opts.logo?.dataUrl ? (
                    <img src={opts.logo.dataUrl} alt="" className="h-full w-full object-contain" />
                  ) : (
                    <Box size={18} className="text-ink-300" />
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" leadingIcon={<Upload size={14} />} onClick={() => logoInput.current?.click()}>
                    {opts.logo?.dataUrl ? "Replace" : "Upload logo"}
                  </Button>
                  {opts.logo?.dataUrl && (
                    <Button variant="ghost" size="sm" leadingIcon={<Trash2 size={14} />} onClick={() => set({ logo: undefined })}>
                      Remove
                    </Button>
                  )}
                </div>
                <input ref={logoInput} type="file" accept="image/*" className="hidden" onChange={(e) => void uploadLogo(e.target.files?.[0])} />
              </div>
              {opts.logo?.dataUrl && (
                <div className="mt-3 space-y-3">
                  <Switch label="Show logo on embed" checked={!!opts.logo?.show} onChange={(e) => set({ logo: { ...opts.logo, show: e.target.checked } })} />
                  <div className="flex gap-3">
                    <Select
                      label="Fit"
                      value={opts.logo?.fit ?? "contain"}
                      onChange={(e) => set({ logo: { ...opts.logo, fit: e.target.value as "contain" | "cover" } })}
                      options={[
                        { value: "contain", label: "Contain" },
                        { value: "cover", label: "Cover" },
                      ]}
                    />
                    <Select
                      label="Position"
                      value={opts.logo?.position ?? "left"}
                      onChange={(e) => set({ logo: { ...opts.logo, position: e.target.value as "left" | "center" | "right" } })}
                      options={[
                        { value: "left", label: "Left" },
                        { value: "center", label: "Center" },
                        { value: "right", label: "Right" },
                      ]}
                    />
                  </div>
                </div>
              )}
              <Switch
                label="Remove “by Jewelitt Vault” backlink"
                hint="Hide the small credit shown on white-labeled embeds."
                checked={!!opts.removeBacklink}
                onChange={(e) => set({ removeBacklink: e.target.checked })}
                className="mt-4"
              />
            </Group>

            <Group title="Loading screen">
              <Select
                label="Loader style"
                value={opts.loaderStyle ?? "spinner"}
                onChange={(e) => set({ loaderStyle: e.target.value as EmbedOptions["loaderStyle"] })}
                options={[
                  { value: "spinner", label: "Spinner" },
                  { value: "logo", label: "Logo pulse" },
                  { value: "progress", label: "Progress bar" },
                ]}
              />
            </Group>
          </div>

          {/* Preview */}
          <div className="space-y-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-400">Preview</p>
            <div
              className="relative flex aspect-[4/3] items-center justify-center overflow-hidden rounded-xl border border-ink-200"
              style={{
                background:
                  opts.background?.type === "color"
                    ? opts.background.color
                    : "repeating-conic-gradient(#f1f1ef 0% 25%, #ffffff 0% 50%) 50% / 18px 18px",
              }}
            >
              {model.posterUrl ? (
                <img src={model.posterUrl} alt="" className="max-h-[80%] max-w-[80%] object-contain" />
              ) : (
                <Box size={28} className="text-ink-300" />
              )}
              {opts.showTitle && (
                <span className="absolute left-3 top-3 rounded-full bg-white/85 px-2.5 py-1 text-[11px] font-semibold text-ink-800 shadow-sm backdrop-blur">
                  {model.name}
                </span>
              )}
              {opts.logo?.show && opts.logo.dataUrl && (
                <img
                  src={opts.logo.dataUrl}
                  alt=""
                  className={`absolute bottom-3 h-7 ${
                    opts.logo.position === "right" ? "right-3" : opts.logo.position === "center" ? "left-1/2 -translate-x-1/2" : "left-3"
                  } ${opts.logo.fit === "cover" ? "object-cover" : "object-contain"}`}
                />
              )}
              {!opts.removeBacklink && (
                <span className="absolute bottom-2 right-3 text-[9px] font-medium tracking-wide text-ink-400">by Jewelitt Vault</span>
              )}
            </div>

            <div className="rounded-xl border border-ink-100 bg-ink-50/50 p-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-400">Loading screen</p>
              <div className="flex h-20 items-center justify-center rounded-lg bg-white">
                <Loader variant={opts.loaderStyle ?? "spinner"} logo={opts.logo?.dataUrl} size={26} />
              </div>
            </div>

            <button onClick={copyHtml} className="flex w-full items-center justify-center gap-2 rounded-lg border border-ink-200 py-2 text-[12.5px] font-medium text-ink-700 transition-colors hover:border-gold-300">
              {copied ? <Check size={14} className="text-emerald" /> : <Copy size={14} />}
              {copied ? "Copied HTML" : "Copy HTML code"}
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 flex items-center justify-end gap-2 border-t border-ink-100 pt-4">
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={save} disabled={saving || loading} shine>
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </Modal>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-400">{title}</h4>
      {children}
    </section>
  );
}

function NumberField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <label className="flex-1 text-[12px] text-ink-600">
      <span className="mb-1 block font-medium">{label}</span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full rounded-lg border border-ink-200 px-3 py-2 text-[13px] text-ink-900 focus:border-gold-400 focus:outline-none"
      />
    </label>
  );
}
